import { BedDouble, Bike, Fuel, Snowflake, Tent, UtensilsCrossed, Zap } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import { PLACE_COLOR, type PlaceKind } from "../lib/mapdata";
import type { UiLang, Stop } from "../lib/types";

interface Props {
  /** Stoppene filteret teller mot — ruta som er valgt. */
  stops: Stop[];
  active: Set<PlaceKind>;
  onChange: (next: Set<PlaceKind>) => void;
  lang: UiLang;
}

/**
 * Rekkefølgen typene står i, og ikonet hver av dem får.
 *
 * Overnatting først, så det praktiske. Det er rekkefølgen spørsmålene kommer i:
 * hvor sover vi, hvor lader vi, hvor spiser vi.
 */
const KINDS: { kind: PlaceKind; icon: LucideIcon }[] = [
  { kind: "hotel", icon: BedDouble },
  { kind: "hostel", icon: Bike },
  { kind: "camping", icon: Tent },
  { kind: "cabin", icon: Tent },
  { kind: "charging", icon: Zap },
  { kind: "fuel", icon: Fuel },
  { kind: "food", icon: UtensilsCrossed },
  { kind: "ski", icon: Snowflake },
];

/**
 * Filter for hva som vises av tjenester på kartet og i stoppdetaljene.
 *
 * Uten det blir mengden et problem. Rundt Bergen ligger det seks hoteller,
 * fire campingplasser, åtte ladere og tolv spisesteder innen rekkevidde, og alt
 * tegnet samtidig gir en sky av punkter over byen der man skulle sett fjorden.
 * Å løse det ved å vise mindre data var feil svar — turisten som spør «hvilke
 * hoteller finnes her» skal få alle. Å la dem velge er det riktige.
 *
 * Tallet på hver knapp er antallet langs *den valgte ruta*, ikke i landet. Det
 * gjør knappen til en opplysning i seg selv: «camping 31» sier at det finnes
 * rikelig, «hostell 2» sier at man bør bestille.
 *
 * Valget lagres i nettleseren, fordi det er en preferanse og ikke en del av
 * ruta — en delt lenke skal vise mottakerens eget filter, ikke avsenderens.
 */
export function PlaceFilter({ stops, active, onChange, lang }: Props) {
  const t = dictionaries[lang];

  const counts = countByKind(stops);
  // Typer ingen av stoppene har er ikke et valg; de ville vært knapper som ikke gjør noe.
  const available = KINDS.filter(({ kind }) => counts[kind] > 0);
  if (available.length === 0) return null;

  const allOn = available.every(({ kind }) => active.has(kind));

  const toggle = (kind: PlaceKind) => {
    const next = new Set(active);
    if (next.has(kind)) next.delete(kind);
    else next.add(kind);
    onChange(next);
  };

  return (
    <div className="place-filter no-print">
      <span className="place-filter-label">{t.filterLabel}</span>

      {available.map(({ kind, icon: Icon }) => {
        const on = active.has(kind);
        return (
          <button
            key={kind}
            type="button"
            className={`place-chip ${on ? "place-chip--on" : ""}`}
            /*
             * Påslått chip fylles med stedstypens egen farge — den samme som
             * punktet på kartet. Den forrige varianten satte bare kantfarge og
             * tekstfarge, og på en hvit bakgrunn ble forskjellen mellom på og
             * av så liten at man ikke kunne se hva som var valgt.
             */
            style={
              on
                ? { background: PLACE_COLOR[kind], borderColor: PLACE_COLOR[kind] }
                : undefined
            }
            aria-pressed={on}
            onClick={() => toggle(kind)}
          >
            <Icon size={12} />
            {t.placeKind[kind]}
            <span className="place-count">{counts[kind]}</span>
          </button>
        );
      })}

      <button
        type="button"
        className="place-chip place-chip--all"
        onClick={() => onChange(allOn ? new Set() : new Set(available.map(({ kind }) => kind)))}
      >
        {allOn ? t.filterNone : t.filterAll}
      </button>
    </div>
  );
}

/**
 * Hvor mange steder av hver type som finnes langs ruta.
 *
 * Samme sted kan ligge nær to stopp og stå i begge listene, så id-ene samles i
 * et sett først — ellers ville et hotell mellom Voss og Bergen blitt telt to
 * ganger, og tallet på knappen ville vært større enn antallet punkter på kartet.
 */
function countByKind(stops: Stop[]): Record<PlaceKind, number> {
  const seen: Record<string, Set<string>> = {};
  const add = (kind: string, id: string) => {
    (seen[kind] ??= new Set()).add(id);
  };

  for (const stop of stops) {
    for (const entry of stop.lodging ?? []) add(entry.kind, entry.id);
    for (const entry of stop.amenities ?? []) add(entry.kind, entry.id);
    for (const area of stop.ski?.areas ?? []) add("ski", area.id);
  }

  return Object.fromEntries(
    KINDS.map(({ kind }) => [kind, seen[kind]?.size ?? 0]),
  ) as Record<PlaceKind, number>;
}

/** Alle typer, til utgangstilstanden. */
export const ALL_PLACE_KINDS: PlaceKind[] = KINDS.map(({ kind }) => kind);
